import {
  SendWhatsAppTextMessageRequest,
  WhatsAppService,
  WorkflowNotificationResource,
  OrganizationService,
  WorkflowsService,
} from '@/api';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { useStore } from '@nanostores/react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { Clock9, Loader2, Send } from 'lucide-react';
import { toast } from 'sonner';
import React, { useMemo, useEffect, useRef, useState, useCallback } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import metaIcon from '@/assets/meta.svg';
import { $org } from '@/stores/orgStore';
import { $profile } from '@/stores/appStore';

export interface WhatsAppMessage {
  id: string;
  threadId?: string | null;
  to: string;
  from: string;
  date: string;
  body: string;
  read: boolean;
  messages?: WhatsAppMessage[];
  phoneNumberId?: string;
  templateName?: string;
  languageCode?: string;
  parameters?: string[];
  resources?: WorkflowNotificationResource[];
}

export interface WhatsAppDetailProps {
  message: WhatsAppMessage | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const isPhoneNumber = (value: string): boolean => {
  const phoneRegex = /^\+?[1-9]\d{1,14}$/;
  return phoneRegex.test(value);
};

const formatDisplayName = (value: string): string => {
  if (isPhoneNumber(value)) {
    return value.startsWith('+') ? value : `+${value}`;
  }
  return value;
};

const getCustomerNumber = (
  msg: WhatsAppMessage,
  orgPhoneNumbers: { phoneNumber: string; phoneNumberId: string }[]
): string => {
  if (!msg || !msg.from || !msg.to) return 'Unknown';
  const fromIsOrg = orgPhoneNumbers.some(p => p.phoneNumber === msg.from);
  return fromIsOrg ? msg.to : msg.from;
};

const WhatsAppDetail: React.FC<WhatsAppDetailProps> = ({ message, open, onOpenChange }) => {
  const org = useStore($org)?.id;
  const profile = useStore($profile);
  const queryClient = useQueryClient();
  const [reply, setReply] = useState('');
  const [threadMessages, setThreadMessages] = useState<Record<string, WhatsAppMessage[]>>({});
  const [lastPolledTimestamp, setLastPolledTimestamp] = useState<Record<string, string>>({});
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Add a set to track locally sent message signatures (content + timestamp)
  const [localMessageSignatures, setLocalMessageSignatures] = useState<Set<string>>(new Set());
  // Add a set to track seen message IDs
  const [seenMessageIds, setSeenMessageIds] = useState<Set<string>>(new Set());

  const currentThreadId = useMemo(() => message?.threadId || 'default', [message]);

  const { data: orgPhoneNumbers = [] } = useQuery({
    queryKey: ['orgPhoneNumbers', org],
    enabled: !!org,
    queryFn: async () => {
      if (!org) return [];
      return await OrganizationService.getOrganizationPhoneNumbers(org);
    },
  });

  // Create a stable mapping of phoneNumberId to phoneNumber
  const phoneNumberIdToPhoneNumber = useMemo(() => {
    const mapping: Record<string, string> = {};
    orgPhoneNumbers.forEach(phone => {
      mapping[phone.phoneNumberId] = phone.phoneNumber;
    });
    return mapping;
  }, [orgPhoneNumbers]);

  // Create a stable set of organization phone numbers for quick lookup
  const orgPhoneNumberSet = useMemo(() => {
    return new Set(orgPhoneNumbers.map(phone => phone.phoneNumber));
  }, [orgPhoneNumbers]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [threadMessages]);

  useEffect(() => {
    if (!org || !message?.threadId || !open) return;

    const fetchInitialThread = async () => {
      try {
        const res = await WorkflowsService.getWorkflowNotifications(
          org,
          undefined,
          'WHATSAPP_NOTIFICATION'
        );
        const threadNotifications = res.filter(
          r => r.threadId === message.threadId
        );

        const transformedMessages = threadNotifications.map(notification => {
          const data = notification.data as any || {};
          return {
            id: notification.id,
            threadId: notification.threadId,
            to: data.to|| '',
            from: data.from || '',
            date: data.message_timestamp || notification.createdAtUtc,
            body: data.message || '',
            read: notification.readStatus === 'READ',
            phoneNumberId: data.phone_number_id,
            templateName: data.templateName,
            languageCode: data.languageCode,
            parameters: data.parameters || [],
            resources: notification.workflowNotificationResources || []
          };
        });

        const sorted = [...transformedMessages].sort(
          (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
        );

        setThreadMessages(prev => ({
          ...prev,
          [currentThreadId]: sorted,
        }));

        // Initialize tracking sets with fresh data
        const messageIds = sorted.map(msg => msg.id);
        const messageSignatures = sorted.map(msg => `${msg.body}_${Math.floor(new Date(msg.date).getTime() / 1000)}`);
        
        setSeenMessageIds(prev => new Set([...prev, ...messageIds]));
        setLocalMessageSignatures(prev => new Set([...prev, ...messageSignatures]));

        // Update last timestamp for polling
        const latest = sorted[sorted.length - 1];
        if (latest?.date) {
          setLastPolledTimestamp(prev => ({
            ...prev,
            [currentThreadId]: latest.date,
          }));
        }

      } catch (error) {
        console.error('Failed to fetch initial thread messages', error);
      }
    };

    fetchInitialThread();
  }, [org, message?.threadId, open, currentThreadId]);

  useEffect(() => {
    if (!org || !message?.threadId || !open) {
      return;
    }

    const interval = setInterval(async () => {
      try {
        const res = await WorkflowsService.getWorkflowNotifications(
          org,
          undefined,
          'WHATSAPP_NOTIFICATION'
        );

        // Filter notifications for the current thread only
        const threadNotifications = res.filter(
          notification => notification.threadId === message.threadId
        );

        if (threadNotifications.length === 0) {
          return;
        }

        // Transform the API response to match our WhatsAppMessage interface
        const transformMessage = (notification: any): WhatsAppMessage => {
          const data = notification.data || {};
          return {
            id: notification.id,
            threadId: notification.threadId,
            to: data.to || '',
            from: data.from || '',
            date: data.message_timestamp || notification.createdAtUtc,
            body: data.message || '',
            read: notification.readStatus === 'READ',
            phoneNumberId: data.phone_number_id,
            templateName: data.template_name,
            languageCode: data.language_code,
            parameters: data.parameters || [],
            resources: notification.workflowNotificationResources || []
          };
        };

        // Transform all thread notifications
        const transformedMessages = threadNotifications.map(transformMessage);

        // Filter out messages we've already seen
        const lastTimestamp = lastPolledTimestamp[currentThreadId];
        const newMessages = lastTimestamp 
          ? transformedMessages.filter(msg => {
              const isNewer = new Date(msg.date) > new Date(lastTimestamp);
              const isNotSeen = !seenMessageIds.has(msg.id);
              // Use a more flexible signature that doesn't rely on exact timestamp
              const messageSignature = `${msg.body}_${Math.floor(new Date(msg.date).getTime() / 1000)}`;
              const isNotLocal = !localMessageSignatures.has(messageSignature);
              return isNewer && isNotLocal && isNotSeen;
            })
          : transformedMessages.filter(msg => {
              const isNotSeen = !seenMessageIds.has(msg.id);
              const messageSignature = `${msg.body}_${Math.floor(new Date(msg.date).getTime() / 1000)}`;
              const isNotLocal = !localMessageSignatures.has(messageSignature);
              return isNotLocal && isNotSeen;
            });

        if (newMessages.length > 0) {
          // Update the last polled timestamp to the latest message timestamp
          const latestNewMessage = newMessages[newMessages.length - 1];
          setLastPolledTimestamp(prev => ({ 
            ...prev, 
            [currentThreadId]: latestNewMessage.date 
          }));

          // Track seen message IDs
          setSeenMessageIds(prev => new Set([...prev, ...newMessages.map(msg => msg.id)]));

          setThreadMessages(prev => {
            const currentThread = prev[currentThreadId] || [];
            
            // Remove any local messages that might be duplicates of the new server messages
            const filteredThread = currentThread.filter(localMsg => {
              // If this is a local message, check if any of the new server messages match it
              if (localMsg.id.startsWith('local-')) {
                const localSignature = `${localMsg.body}_${Math.floor(new Date(localMsg.date).getTime() / 1000)}`;
                const hasServerDuplicate = newMessages.some(serverMsg => {
                  const serverSignature = `${serverMsg.body}_${Math.floor(new Date(serverMsg.date).getTime() / 1000)}`;
                  return localSignature === serverSignature;
                });
                return !hasServerDuplicate; // Keep local message only if no server duplicate exists
              }
              return true; // Keep all non-local messages
            });
            
            const updatedThread = [...filteredThread, ...newMessages];
            // Re-sort the entire thread to maintain chronological order
            const finalSorted = updatedThread.sort(
              (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
            );
            return {
              ...prev,
              [currentThreadId]: finalSorted,
            };
          });
        }
      } catch (err) {
        console.error('Polling WhatsApp messages failed:', err);
      }
    }, 5000);

    return () => {
      clearInterval(interval);
    };
  }, [org, message?.threadId, message?.id, currentThreadId, open, lastPolledTimestamp, localMessageSignatures, seenMessageIds]);

  const sendMessage = useMutation({
    mutationFn: async (payload: SendWhatsAppTextMessageRequest) => {
      if (!org) throw new Error('Organization not found');
      return await WhatsAppService.sendWhatsAppTextMessage(org, payload);
    },
    onSuccess: async (_, variables) => {
      const latest = threadMessages[currentThreadId]?.slice(-1)[0] || message;
      
      // Use the stable mapping to get the organization's phone number
      const orgPhoneNumber = latest?.phoneNumberId ? phoneNumberIdToPhoneNumber[latest.phoneNumberId] : null;
      const localMessageId = `local-${Date.now()}`;
      const messageTimestamp = new Date().toISOString();
      
      const sentMessage: WhatsAppMessage = {
        id: localMessageId,
        to: variables.to || latest?.from || '',
        from: orgPhoneNumber || latest?.to || message?.to || '',
        date: messageTimestamp,
        body: variables.messageText || '',
        read: true,
        phoneNumberId: latest?.phoneNumberId,
      };
      
      // Create a signature based on content and timestamp (rounded to seconds) to prevent duplicates
      const messageSignature = `${variables.messageText}_${Math.floor(new Date(messageTimestamp).getTime() / 1000)}`;
      setLocalMessageSignatures(prev => new Set([...prev, messageSignature]));
      
      // Also track the local message ID
      setSeenMessageIds(prev => new Set([...prev, localMessageId]));
      
      setThreadMessages(prev => {
        const currentThread = prev[currentThreadId] || [];
        const newThread = [...currentThread, sentMessage];
        return {
          ...prev,
          [currentThreadId]: newThread,
        };
      });
      setReply('');
      await queryClient.invalidateQueries({
        queryKey: ['getNotifications', org, undefined, 'WHATSAPP_NOTIFICATION'],
      });
    },
    onError: (error) => {
      console.error('Failed to send WhatsApp message:', error);
      toast.error('Failed to send WhatsApp message.');
    },
  });

  // Update the isMessageFromOrg function to handle the correct logic
  const isMessageFromOrg = useCallback((msg: WhatsAppMessage): boolean => {
    // For outgoing messages from organization: from = phoneNumberId
    // For incoming messages from customer: from = customer phone number
    
    // Check if the from field is a phoneNumberId (organization message)
    if (msg.phoneNumberId && msg.from === msg.phoneNumberId) {
      return true;
    }
    
    // Check if the from field matches an org phone number (incoming org message)
    if (orgPhoneNumberSet.has(msg.from)) {
      return true;
    }
    
    return false;
  }, [orgPhoneNumberSet]);

  // Update the getCustomerNumberForMessage function
  const getCustomerNumberForMessage = useCallback((msg: WhatsAppMessage): string => {
    // For outgoing messages: to = customer number
    // For incoming messages: from = customer number
    return isMessageFromOrg(msg) ? msg.to : msg.from;
  }, [isMessageFromOrg]);

  if (!message || !message.date || !message.from) return null;

  const sortedConversation = threadMessages[currentThreadId] || [];
  const latestMessage = sortedConversation[sortedConversation.length - 1];
  const phoneNumberId = message.phoneNumberId || latestMessage?.phoneNumberId;

  return (
    <Dialog open={open} onOpenChange={onOpenChange} modal>
      <DialogContent className="flex max-h-[90vh] max-w-[650px] flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center">
            <Avatar>
              <AvatarImage src={metaIcon} className="dark:bg-muted-foreground" />
              <AvatarFallback>f</AvatarFallback>
            </Avatar>
            <div className="w-[200px] truncate">{formatDisplayName(getCustomerNumberForMessage(message))}</div>
          </DialogTitle>
          <DialogDescription>
            <div className="mt-1 flex items-center text-xs text-gray-500">
              <Clock9 className="mr-1 h-3 w-3" />
              {message.date ? new Date(message.date).toLocaleString() : 'N/A'}
            </div>
          </DialogDescription>
        </DialogHeader>

        <div className="flex h-full flex-col overflow-y-auto px-2 space-y-2">
          {sortedConversation.map(msg => {
            const isFromUser = isMessageFromOrg(msg);
            const senderName = isFromUser ? profile?.name || 'You' : formatDisplayName(msg.from);

            return (
              <div
                key={msg.id}
                className={`flex ${isFromUser ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`max-w-[80%] rounded-md border p-4 shadow-sm ${
                  isFromUser ? 'bg-green-100' : 'bg-white'
                }`}>
                  <div className="text-sm text-muted-foreground">
                    <div className="flex justify-between text-xs text-gray-500">
                      <div>
                        {isFromUser
                          ? `To: ${formatDisplayName(msg.to)} (${profile?.name || 'You'})`
                          : `From: ${senderName}`}
                      </div>
                      <div className="flex items-center">
                        <Clock9 className="mr-1 h-3 w-3 p-0.5" />
                        {msg.date ? new Date(msg.date).toLocaleString() : 'N/A'}
                      </div>
                    </div>
                    <p className="mt-2 whitespace-pre-line text-sm text-gray-800">{msg.body}</p>
                    {msg.resources && msg.resources.length > 0 && (
                      <div className="mt-4 text-sm text-gray-600">
                        <p className="font-medium">Attachments:</p>
                        <div className="mt-1 flex flex-wrap gap-2">
                          {msg.resources.map((res, idx) => (
                            <a
                              key={idx}
                              href={res.downloadUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:underline"
                            >
                              {res.fileName}
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
          <div ref={messagesEndRef} />
        </div>

        <DialogFooter className="flex w-full">
          <div className="flex w-full flex-col gap-2">
            <Textarea
              placeholder="Type your message here."
              rows={3}
              className="w-full"
              value={reply}
              onChange={e => setReply(e.target.value)}
            />
            <div className="flex justify-end">
              <Button
                variant="default"
                size="sm"
                disabled={sendMessage.isPending}
                onClick={async () => {
                  if (!phoneNumberId || !latestMessage) {
                    return toast.error('Missing WhatsApp data.');
                  }
                  const customerNumber = getCustomerNumberForMessage(latestMessage);
                  await sendMessage.mutateAsync({
                    phoneNumberId: phoneNumberId,
                    to: customerNumber,
                    messageText: reply,
                  });
                }}
              >
                {sendMessage.isPending ? (
                  <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                ) : (
                  <Send className="mr-2 h-3 w-3" />
                )}
                Send
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default WhatsAppDetail;
